import { InputAdornment } from '@mui/material';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import {
  ariaDescribedByIds,
  BaseInputTemplateProps,
  examplesId,
  FormContextType,
  getInputProps,
  labelValue,
  RJSFSchema,
  StrictRJSFSchema,
} from '@rjsf/utils';
import { ChangeEvent, FocusEvent } from 'react';
import { renderWithEval } from 'utils/utils';

const TYPES_THAT_SHRINK_LABEL = ['date', 'datetime-local', 'file', 'time'];

/** Original source: https://github.com/rjsf-team/react-jsonschema-form/blob/main/packages/mui/src/BaseInputTemplate/BaseInputTemplate.tsx */


/** The `BaseInputTemplate` is the template to use to render the basic `<input>` component for the `core` theme.
 * It is used as the template for rendering many of the <input> based widgets that differ by `type` and callbacks only.
 * It can be customized/overridden for other themes or individual implementations as needed.
 *
 * @param props - The `WidgetProps` for this template
 */
export default function CustomInputWidget<
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = any
>(props: BaseInputTemplateProps<T, S, F>) {
  const {
    id,
    name, // remove this from textFieldProps
    placeholder,
    required,
    readonly,
    disabled,
    type,
    label,
    hideLabel,
    hideError,
    value,
    onChange,
    onChangeOverride,
    onBlur,
    onFocus,
    autofocus,
    options,
    schema,
    uiSchema,
    rawErrors = [],
    errorSchema,
    formContext,
    registry,
    InputProps,
    InputLabelProps,
    ...textFieldProps
  } = props;
  const inputProps = getInputProps<T, S, F>(schema, type, options);
  const { step, min, max, ...rest } = inputProps;
  const _onChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
    onChange(value === '' ? options.emptyValue : value);
  const _onBlur = ({ target }: FocusEvent<HTMLInputElement>) => onBlur(id, target && target.value);
  const _onFocus = ({ target }: FocusEvent<HTMLInputElement>) => onFocus(id, target && target.value);
  const DisplayInputLabelProps = TYPES_THAT_SHRINK_LABEL.includes(type)
    ? {
      ...InputLabelProps,
      shrink: true,
    }
    : InputLabelProps;

  let startAdornment = uiSchema && uiSchema["ui:options"]?.prefix as string;
  let endAdornment = uiSchema && uiSchema["ui:options"]?.suffix as string;

  if (startAdornment) {
    startAdornment = renderWithEval(startAdornment, formContext);
  }

  if (endAdornment) {
    endAdornment = renderWithEval(endAdornment, formContext);
  }

  const CustomInputProps = {
    startAdornment: startAdornment ? <InputAdornment position="start">{startAdornment}</InputAdornment> : null,
    endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : null,
    ...InputProps
  }

  return (
    <>
      <TextField
        id={id}
        sx={{
          '& .MuiInputAdornment-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.10)',
            padding: '0 8px',
            borderRadius: '10px',
            height: '100%',
            paddingTop: '2px',
            paddingBottom: '2px'
          },
          '& .MuiInputAdornment-root p': {
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: '0.8rem',
            paddingTop: '7px',
            paddingBottom: '7px',
            paddingLeft: '5px',
            paddingRight: '5px'
          },
        }}
        name={id}
        fullWidth
        placeholder={placeholder}
        label={labelValue(label || undefined, hideLabel, undefined)}
        autoFocus={autofocus}
        required={required}
        disabled={disabled || readonly}
        {...rest}
        value={value || value === 0 ? value : ''}
        error={rawErrors.length > 0}
        onChange={onChangeOverride || _onChange}
        onBlur={_onBlur}
        onFocus={_onFocus}
        InputProps={CustomInputProps}
        InputLabelProps={DisplayInputLabelProps}
        {...(textFieldProps as TextFieldProps)}
        aria-describedby={ariaDescribedByIds<T>(id, !!schema.examples)}
      />
      {Array.isArray(schema.examples) && (
        <datalist id={examplesId<T>(id)}>
          {(schema.examples as string[])
            .concat(schema.default && !schema.examples.includes(schema.default) ? ([schema.default] as string[]) : [])
            .map((example: any) => {
              return <option key={example} value={example} />;
            })}
        </datalist>
      )}
    </>
  );
}